import {Injectable} from '@angular/core';
import {HttpService} from '../http.service';
import { environment } from 'src/environments/environment';
import {Selections} from '../../../shared-gen/Model/Selections/Selections';
import {DeviceStatus} from '../../../shared-gen/Device/Status/DeviceStatus';
import { Menu } from "../../../shared-gen/Model/Menus/Menu";
import {UUID} from "angular2-uuid";

/**
 * API endpoints for retrieving different status information and controlling the Device.
 */
@Injectable({
	providedIn: 'root'
})
export class DeviceApi extends HttpService {

	/**
	 * Gets the device status by long polling.
	 * The last md5 hash of the status is given to enable long polling.
	 * The method returns as soon as the status has changed (different md5 hash) or when
	 * the maximum response time is over.
	 * The MD5 hash of the returned status is saved into the response header "X-MD5".
	 */
	async getStatusDelayed(lastMd5: string): Promise<[string, DeviceStatus]> {
		const response = await this.http.get<DeviceStatus>(
			`${this.serverUrl}/device/status` +
			`?lastmd5=${lastMd5}&maxtime=${environment.webApi.statusMaxResponseTimeMs}`,
			{headers: {}, observe: 'response'}
		).toPromise();
		return [response.headers.get('x-md5'), response.body];
	}

	/**
	 * Skips the detection of a cup when in [VmcState.CupPlacing].
	 */
	async skipCup(): Promise<void> {
		await this.post<void>('device/skipcup');
	}

	/**
	 * Reports a user action to reset the inactive timer,
	 * e.g. when the user has clicked something on the screen.
	 */
	async userAction(): Promise<void> {
		await this.post<void>('device/useraction');
	}

	/**
	 * Gets the current menu.
	 */
	async getMenu(): Promise<Menu> {
		return await this.get<Menu>('device/menu');
	}

	/**
	 * Gets the current selections.
	 */
	async getSelections(): Promise<Selections> {
		return await this.get<Selections>('device/selections');
	}

	/**
	 * Sets the current selections on the machine.
	 * Call this method from the App UI, e.g. when a technician edited the selections.
	 * This will overwrite the current selections and will sync the new config
	 * to the backend as soon as possible.
	 */
	async setSelections(selections: Selections): Promise<void> {
		await this.post<void>('device/selections', selections);
	}

	/**
	 * Starts the data update, if one is available and prepared.
	 * See DataUpdateStatus.
	 */
	async startDataUpdate(): Promise<void> {
		await this.post<void>('device/dataupdate');
	}

}
